<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('master.tooltipCommon.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <span class="tti-viewTitle">
            <!-- エリア別分析 -->
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('operationalPerformance.analysisByAreaTitle') }}
            <!-- グラフ表示(エリア別) -->
            <span class="tti-viewTitle-chartarea" v-if="prefectureRouteQuery.level === areaRole.AREA">
              <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
              <tti-svg-icon icon-name="wo_016"></tti-svg-icon>
              {{ $t('operationalPerformance.showGraphArea') }}
            </span>
            <!-- グラフ表示(都道府県別) -->
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
            <tti-svg-icon icon-name="wo_016"></tti-svg-icon>
            {{ $t('operationalPerformance.showGraphPrefecture') }}
          </span>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title">
        <span class="contentTitle">
          <span>{{ $t('operationalPerformance.prefecture') }}</span>
          <span v-if="prefectureRouteQuery.level === areaRole.AREA">{{
            `（${this.prefectureRouteQuery.label}）`
          }}</span>
        </span>
      </div>
      <ttiCharts class="charts" :data="moveResult" @onChartContextClick="onChartContextClick"></ttiCharts>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 印刷 -->
        <el-button
          class="tti-btn-default tti-btn-002"
          @click="onPdfBtnClick"
          v-tooltip="{
            content: `${$t('operationalPerformance.printing')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <tti-svg-icon icon-name="he_008"></tti-svg-icon>
          {{ $t('buttons.print') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { AxiosResponse } from 'axios'

import { i18n } from '@/locales'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import ttiCharts from '@/components/ttiCharts.vue'
import {
  MoveResult,
  ResponseData,
  PrefectureAllocData,
  PrefectureRouteQuery,
  PrefectureMasterData,
  OpPerfAreaPrefectureParam
} from '@/models'
import {
  getAreaMasterDataWhoHasSubPrefecture,
  opPerfPrefecturePDFDownload,
  getOpPerfAreaPrefecture
} from '@/store/apis'
import config from '@/config/config'
import { changeBlobToJson } from '@/store/apis/apiClient'
import { AreaRole, DeleteType } from '@/commons/constants'
import helperService from '@/service/helperService'
import { correctTimeFormat, isDateAfter, isDateDayOver } from '@/utils/dateUtils'

/**
 * 都道府県画面
 *
 * @class
 */
@Component({
  name: 'chart-prefecture',
  components: { ttiCharts }
})
export default class CHART_PREFECTURE extends Vue {
  areaRole = AreaRole
  /**
   * 都道府県ルーティングパラメータ定義
   */
  prefectureRouteQuery: PrefectureRouteQuery = {
    label: '',
    level: '',
    areaId: '',
    prefectureIdList: [],
    operationStartDay: '',
    operationEndDay: '',
    deleteFlg: ''
  }

  /**
   * 稼働実績データ
   */
  moveResult: MoveResult = {
    moveResult: []
  }

  /**
   * 都道府県情報
   */
  prefectureMasterData: PrefectureMasterData[] = []

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('CHART_PREFECTURE > mounted')
    this.initPrefecture()
    if (!this.routeQueryCheck()) {
      this.movepage()
      return
    }
    await this.fetchAreaOptions()
    this.areaNotExistProxy(this.handleShowGraph)
  }

  /**
   * URLから都道府県取得
   *
   * @returns {void}
   */
  initPrefecture(): void {
    this.prefectureRouteQuery = {
      label: this.$route.query.label as string,
      level: this.$route.query.level as string,
      areaId: this.$route.query.areaId as string,
      prefectureIdList: this.$route.query.prefectureIdList as [],
      operationStartDay: this.$route.query.operationStartDate as string,
      operationEndDay: this.$route.query.operationEndDate as string,
      deleteFlg: this.$route.query.deleteFlg as string
    }
  }

  /**
   * パラメータ着信がない場合
   *
   * @returns {boolean} パラメータ着信がない場合
   */
  routeQueryCheck(): boolean {
    if (this.prefectureRouteQuery.level !== AreaRole.AREA && this.prefectureRouteQuery.level !== AreaRole.PREFECTURE) {
      return false
    }
    if (
      !this.prefectureRouteQuery.operationStartDay ||
      !this.prefectureRouteQuery.operationEndDay ||
      !this.prefectureRouteQuery.deleteFlg
    ) {
      return false
    }
    if (this.prefectureRouteQuery.level === AreaRole.AREA && !this.prefectureRouteQuery.areaId) {
      return false
    }
    if (
      this.prefectureRouteQuery.deleteFlg !== DeleteType.notdeleteData &&
      this.prefectureRouteQuery.deleteFlg !== DeleteType.deleteData
    ) {
      return false
    }
    if (
      this.prefectureRouteQuery.level === AreaRole.PREFECTURE &&
      (!this.prefectureRouteQuery.prefectureIdList || this.prefectureRouteQuery.prefectureIdList.length === 0)
    ) {
      return false
    }
    if (
      !correctTimeFormat(this.prefectureRouteQuery.operationStartDay) ||
      !correctTimeFormat(this.prefectureRouteQuery.operationEndDay) ||
      isDateAfter(this.prefectureRouteQuery.operationStartDay, this.prefectureRouteQuery.operationEndDay) ||
      isDateDayOver(this.prefectureRouteQuery.operationEndDay, this.prefectureRouteQuery.operationStartDay, 31)
    ) {
      return false
    }

    return true
  }

  /**
   * パラメータが間違っている場合の処理
   */
  movepage(): void {
    msgBoxService.info(i18n.t('vaildate.common.E00092').toString())
    this.$router.push(config.menu.areaAnalysis)
  }

  /**
   * エリア情報を取得する
   *
   * @returns {void}
   */
  async fetchAreaOptions(): Promise<void> {
    const data = (await getAreaMasterDataWhoHasSubPrefecture()) as PrefectureAllocData
    data.areaList.forEach(item => {
      this.prefectureMasterData = this.prefectureMasterData.concat(item.prefectureList as PrefectureMasterData[])
    })
    this.$log.debug('CHART_PREFECTURE > fetchAreaOptions', data)
  }

  /**
   * 戻るボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onBackButtonClick(): Promise<void> {
    window.history.back()
    this.$log.debug('CHART_PREFECTURE > onBackButtonClick')
  }

  /**
   * APIを呼び出すときに、エリアが存在しない可能性があります、これらのAPIをプロキシする。
   *
   * @param {any} target プロキシの目標処理
   * @returns {Promise<void>}
   */
  // eslint-disable-next-line
  async areaNotExistProxy(target: any): Promise<void> {
    try {
      await target()
    } catch (e) {
      this.$log.debug('CHART_PREFECTURE > areaNotExistProxy: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // エリアが存在しない場合、エリア別分析画面へ遷移する。
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00091') {
          this.$router.push(config.menu.areaAnalysis)
        }
      })
      throw e
    }
  }

  /**
   * パラメータを取得する。
   *
   * @returns {OpPerfAreaPrefectureParam} パラメータ
   */
  getparam(): OpPerfAreaPrefectureParam {
    const level = this.prefectureRouteQuery.level
    const param: OpPerfAreaPrefectureParam = {
      operationStartDate: this.prefectureRouteQuery.operationStartDay,
      operationEndDate: this.prefectureRouteQuery.operationEndDay,
      deleteFlg: this.prefectureRouteQuery.deleteFlg
    }
    if (level === AreaRole.AREA) {
      param.areaLevel = 0
      param.areaId = this.prefectureRouteQuery.areaId
    }

    if (level === AreaRole.PREFECTURE) {
      param.areaLevel = 1
      if (Array.isArray(this.prefectureRouteQuery.prefectureIdList)) {
        param.prefectureIdList = this.prefectureRouteQuery.prefectureIdList
      } else {
        param.prefectureIdList = [this.prefectureRouteQuery.prefectureIdList]
      }
    }
    return param
  }

  /**
   * 都道府県画面の処理
   *
   * @returns {void}
   */
  async handleShowGraph(): Promise<void> {
    const masters = await getOpPerfAreaPrefecture(this.getparam())
    this.moveResult.moveResult = masters.prefectureList.map(item => {
      return {
        id: item.prefectureId,
        label: this.findResultPrefectureName(item.prefectureId),
        result: (item.operatingRate * 100).toFixed(1)
      }
    })
  }

  /**
   * エリアリストから都道府県名を取得する
   *
   * @param {string} id  都道府県ID
   * @returns {void}
   */
  findResultPrefectureName(id: string): string {
    return helperService.getDefinitionName(this.prefectureMasterData, id, {
      code: 'id',
      value: 'prefectureName'
    })
  }

  /**
   * グラフの内容をクリックした時のコールバック
   *
   * @param {any} result クリックしたデータ
   * @returns {void}
   */
  // eslint-disable-next-line
  async onChartContextClick(result: any): Promise<void> {
    this.$router.push({
      path: config.menu.analysisByManagementObject,
      query: {
        level: this.prefectureRouteQuery.level,
        label: result.moveResult.label,
        prefectureId: result.moveResult.id,
        operationStartDate: this.prefectureRouteQuery.operationStartDay,
        operationEndDate: this.prefectureRouteQuery.operationEndDay,
        deleteFlg: this.prefectureRouteQuery.deleteFlg
      }
    })
  }

  onPdfBtnClick(): void {
    try {
      const confirmCallback = () => {
        this.prefecturePDFDownloadApi()
      }
      msgBoxService.confirm(i18n.t('operationalPerformance.I00160').toString(), confirmCallback)
    } catch (e) {
      this.$log.debug('CHART_PREFECTURE > onPdfBtnClick: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // エリアが存在しない場合、エリア別分析画面へ遷移する。
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00091') {
          this.$router.push(config.menu.areaAnalysis)
        }
      })
      throw e
    }
  }

  async prefecturePDFDownloadApi(): Promise<void> {
    const res = await opPerfPrefecturePDFDownload(this.getparam())
    HelperService.download(res.fileName, res.data)
  }
}
</script>

<style lang="scss" scoped>
.tti-dialog-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #909399;
}

.tti-viewTitle-chartarea {
  display: flex;
  align-items: center;
}

.tti-content-title {
  font-size: $FS02;
}

.tti-padding-content {
  background-color: #f2f2f2;

  .charts {
    background-color: #fff;
  }
}
</style>
